// RGR10 엑셀 업로드 — 1.0 TOOL_IMP 파리티(ToolBar.onChangeHandler → CommonUtil.onImportExcel).
// 서버 업로드 API 는 2.0 에도 없다 — xlsx 를 클라에서 파싱해 한글 헤더를 저장 API 필드 키로 매핑하고
// "신규 행"(edited='I')으로 표에 얹는다. 저장은 7단계 경로(useReturnMonitoringRows)를 그대로 탄다.
import dayjs from 'dayjs'
import ExcelJS from 'exceljs'
import { ReturnMonitoringItemDto } from '@/services/return/returnMonitoring/types'
import { RETURN_MONITORING_COLUMNS } from './columnDefinitions'

/**
 * 화면이 만든 파싱 오류 — 문구를 그대로 보여 준다. 그 밖의 예외(깨진 파일의 라이브러리 오류 등)는 화면이 고정 문구로 바꾼다.
 * `isRow` — 특정 행을 짚는 오류. 안내 기준상 행 짚는 검증은 알럿(notice.rowInvalid)이라 다른 파싱 오류와 가른다.
 */
export class ExcelImportError extends Error {
  readonly isRow: boolean

  constructor(message: string, isRow = false) {
    super(message)
    this.isRow = isRow
  }
}

/**
 * 업로드 반영 대상 — 저장 API(3.3) 의 INSERT 가 받는 필드만 수용한다.
 * 참조번호 쌍(`giRefNo1`/`giRefNo2`)은 업로드 양식에 넣지 않는다(피드백 10) — 바코드+원송장(2순위 매칭)만 지원.
 * TODO(spec): 운영 엑셀 업로드 양식(참조번호 쌍 헤더 이름) 확보 후 칸을 추가한다 — 10장 항목 2.
 */
const IMPORTABLE_KEYS = new Set([
  'sprNm', // 행 화주(화주코드) — 화주가 섞인 업로드를 한 번에 저장한다(LOGISPRD-14900)
  'clsDate',
  'orgInvoiceNo',
  'barcode',
  'rtnQty',
  'carrier',
  'rtnInvoiceNo',
  'claimType',
  'customerClaim',
  'centerClaim',
  'receiptDate',
  'etc1',
  'companyOrdNo',
  'companyOrdOptNo',
  'refOrdNo',
  'refOrdOptNo',
  'rtnWhNm',
])

/**
 * 표 컬럼 한글 제목 → 2.0 필드 키. 업로드 헤더가 표 컬럼 제목(=다운로드 양식 헤더)과 같다고
 * 가정하고 인식한다 — BE `ReturnMonitoringExcelDto.kt`(다운로드 헤더 32종의 실제 소유자)가 이
 * 레포에 없어 실측 대조는 못 했다. TODO(verify): 실제 다운로드 파일을 받아 헤더 문자열을 대조한다.
 * 한 글자라도 다르면 해당 컬럼은 통째로 인식되지 않고, 전부 다르면 헤더 인식 자체가 실패한다.
 */
const titleToKey = new Map(
  RETURN_MONITORING_COLUMNS.filter((c) => typeof c.dataIndex === 'string').map((c) => [
    typeof c.title === 'string' ? c.title : '',
    c.dataIndex as string,
  ]),
)

/** 행 오류 문구에 내부 필드명 대신 컬럼 제목을 쓴다. */
const keyToTitle = new Map([...titleToKey].map(([title, key]) => [key, title]))

const NUMBER_KEYS = new Set(['rtnQty'])

/** 저장 API(3.3) 가 `yyyyMMdd` 8자 정확 일치를 요구하는 필드 — `clsDate` 뿐이다(`receiptDate` 는 길이만 18자 제한). */
const DATE_KEYS = new Set(['clsDate'])

/**
 * 날짜 셀을 `yyyyMMdd` 로 정규화한다. 이미 8자 숫자면 그대로 두고, 아니면 dayjs 로 파싱해 되돌린다
 * (엑셀 날짜형 셀은 `cellValueToText` 가 ISO 문자열로 넘겨주므로 여기서 파싱된다). 파싱할 수 없는
 * 값은 원문을 그대로 반환해 `validateRowForSave` 가 저장 시점에 행 단위로 막는다(서버 왕복 전 차단).
 */
const normalizeClsDate = (text: string): string => {
  if (/^\d{8}$/.test(text)) return text
  const parsed = dayjs(text)
  return parsed.isValid() ? parsed.format('YYYYMMDD') : text
}

/** 엑셀 셀 값(리치텍스트·수식 등)을 안전하게 문자열로 옮긴다 — 객체 기본 toString 에 기대지 않는다. */
const cellValueToText = (value: ExcelJS.CellValue): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object' && 'text' in value) return String((value as { text: unknown }).text)
  if (typeof value === 'object' && 'result' in value) return cellValueToText((value as { result: ExcelJS.CellValue }).result)
  return ''
}

export const parseReturnMonitoringExcel = async (file: File): Promise<Partial<ReturnMonitoringItemDto>[]> => {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return []

  const headerKeys: (string | null)[] = []
  ws.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    const key = titleToKey.get(cellValueToText(cell.value).trim()) ?? null
    headerKeys[col] = key && IMPORTABLE_KEYS.has(key) ? key : null
  })
  if (!headerKeys.some(Boolean)) {
    throw new ExcelImportError('헤더를 인식할 수 없습니다. 엑셀 다운로드 파일과 같은 1행 헤더가 필요합니다.')
  }

  const rows: Partial<ReturnMonitoringItemDto>[] = []
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return
    const item: Record<string, string | number> = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellValueToText(cell.value).trim()
      if (!text) return
      hasValue = true
      if (NUMBER_KEYS.has(key)) {
        const num = Number(text)
        // NaN 을 0 으로 조용히 치환하지 않는다 — 반품수량에 숫자가 아닌 값이 들어오면 사용자에게 알려야
        // 한다(무음 0 치환은 validateRowForSave 의 `rtnQty < 0` 검증도 우회한다).
        if (Number.isNaN(num)) throw new ExcelImportError(`${rowNumber}행의 "${text}" 값이 숫자가 아닙니다(${keyToTitle.get(key)}).`, true)
        item[key] = num
      } else if (DATE_KEYS.has(key)) {
        item[key] = normalizeClsDate(text)
      } else {
        item[key] = text
      }
    })
    if (hasValue) rows.push(item as Partial<ReturnMonitoringItemDto>)
  })
  return rows
}
